// An exam's rubric as more than one page shows it (RFC-048, RFC-055,
// byj-1wv.51.20): the exam's own page and an assignment's, so neither imports
// the other.
import type { ExamMeta } from "../api";
import { competencies } from "../competencies";
import { t, word } from "../i18n";

/** The rubric: its parts as one bar, sized by their points, then each part's points and what earns them. */
export function Rubric({ rubric }: { rubric: NonNullable<ExamMeta["rubric"]> }) {
  const eff = Object.entries(rubric.efficiency ?? {});
  const fluency = rubric.profile === "fluency";
  const parts: { k: string; points: number; more?: string }[] = fluency
    ? [{ k: "outcome", points: rubric.outcome ?? 0 }, ...competencies.map((k) => ({ k, points: rubric.competencies?.[k]?.points ?? 0 }))]
    : [
        { k: "accuracy", points: rubric.accuracy ?? 0 },
        { k: "agent", points: rubric.agent ?? 0 },
        { k: "efficiency", points: eff.reduce((n, [, v]) => n + v, 0), more: eff.length > 0 ? eff.map(([k, v]) => `${word("exams:effLabel", k) ?? k} ${v}`).join(" + ") : undefined },
        { k: "process", points: rubric.process ?? 0 },
      ];
  const total = parts.reduce((n, p) => n + p.points, 0) || 1;
  return (
    <div className="stack" data-testid="exam-rubric">
      <div className="rubric-bar" aria-hidden="true">
        {parts.map((p) => (
          <span key={p.k} className={`rubric-seg rubric-seg-${p.k}`} style={{ flexGrow: p.points }} title={`${word("exams:rubricPart", p.k)} ${p.points}`}>
            {p.points / total >= 0.1 ? p.points : ""}
          </span>
        ))}
      </div>
      <ul className="rubric-parts flush">
        {parts.map((p) => (
          <li key={p.k}>
            <span className="rubric-part">
              <span className={`rubric-dot rubric-seg-${p.k}`} />
              {word("exams:rubricPart", p.k)} {p.points}
            </span>
            {p.more && <span className="muted small"> ({p.more})</span>}
            <span className="muted small rubric-help">{word("exams:rubricHelp", p.k)}</span>
          </li>
        ))}
      </ul>
      {fluency ? (
        <p className="muted small flush">{t("exams:fluencyReported")}</p>
      ) : (
        rubric["zero-at"] != null && <p className="muted small flush">{t("exams:zeroAt", { x: rubric["zero-at"] })}</p>
      )}
    </div>
  );
}
